/**
 * Chainlit Copilot sidebar widget lifecycle for the Analyst panel
 * (CHAINLIT_EMBED_MODE = "sidebar").
 *
 * Unlike the abi-analyst-frontend integration (where the widget draws its own
 * fixed side panel), web2.0 already has panel chrome — .analyst-panel with a
 * header, close button, slide animation and responsive widths. So here the
 * widget host element is RELOCATED into .analyst-panel-body after mounting,
 * and copilot-web2.css (served by just-ask-chainlit, loaded inside the
 * widget's shadow root) flattens the widget's own fixed panel to simply fill
 * that container. Opening/closing stays the platform's job; the widget itself
 * is always "open".
 *
 * Conversation identity (topic/subject/features) travels in the JWT and is
 * read once per session. Chainlit resumes a socket session by session id —
 * which the widget script keeps in module state — so switching projects
 * requires a HARD remount: remove the widget DOM *and* the script tag, then
 * re-inject, so a fresh session id is created and the new JWT is honoured.
 */

const SCRIPT_ID = "chainlit-copilot-script";
const WIDGET_ID = "chainlit-copilot";
const COPILOT_CSS_VERSION = "7";
const FONT_LINK_ID = "chainlit-copilot-font";
// The same URL the copilot bundle requests for itself; see ensureInterFont.
const INTER_FONT_HREF =
  "https://fonts.googleapis.com/css2?family=Inter:wght@400;500;700&display=swap";
const REFERRER_POLICY = "strict-origin-when-cross-origin";

let current = null; // { accessToken }

function removeWidgetDom() {
  if (typeof window.unmountChainlitWidget === "function") {
    try {
      window.unmountChainlitWidget();
    } catch (e) {
      /* already gone */
    }
  }
  const host = document.getElementById(WIDGET_ID);
  if (host) host.remove();
  // The widget pushes the page via an inline margin on <body> while open.
  // Its own unmount cleanup doesn't always restore it (ordering race), and
  // once the widget DOM is gone the body:has(...) neutraliser stops
  // matching, leaving the page shrunk. Clear it explicitly.
  document.body.style.marginRight = "";
  document.body.style.transition = "";
}

function removeScript() {
  const script = document.getElementById(SCRIPT_ID);
  if (script) script.remove();
  delete window.mountChainlitWidget;
  delete window.unmountChainlitWidget;
  delete window.toggleChainlitCopilot;
  delete window.sendChainlitMessage;
}

/**
 * Register Inter in the HOST document so the widget can actually use it.
 *
 * The copilot bundle appends this very stylesheet to its own shadow root
 * (it does that whenever the server's theme.json declares no custom_fonts),
 * but Chrome ignores @font-face rules that live in a shadow tree: the sheet
 * loads and the face is silently never registered. Measured in a harness —
 * `font-family:'Inter'` inside the shadow root rendered at the generic
 * sans-serif fallback's exact width until the same <link> was added to
 * document.head, after which Inter applied inside the shadow root.
 *
 * Font faces registered on the document ARE visible to shadow trees, so this
 * one line is what lets copilot-abi.css's `--font-sans: 'Inter', …` resolve
 * to Inter and match the typography of the iframe this widget replaced.
 * Without it the token quietly falls through to the platform's system stack.
 */
function ensureInterFont() {
  if (document.getElementById(FONT_LINK_ID)) return;
  const link = document.createElement("link");
  link.id = FONT_LINK_ID;
  link.rel = "stylesheet";
  link.referrerPolicy = REFERRER_POLICY;
  link.href = INTER_FONT_HREF;
  document.head.appendChild(link);
}

function injectScript(chainlitServer) {
  return new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.id = SCRIPT_ID;
    script.referrerPolicy = REFERRER_POLICY;
    script.src = `${chainlitServer}/copilot/index.js`;
    script.onload = resolve;
    script.onerror = () =>
      reject(new Error("Failed to load the Chainlit Copilot script"));
    document.body.appendChild(script);
  });
}

/**
 * Mount the Copilot widget inside the given container (the panel body).
 * Idempotent for the same access token — repeat calls while mounted no-op,
 * so the caller can invoke it from an effect without guards.
 *
 * @param {object} opts
 * @param {string} opts.chainlitServer  e.g. "https://chainlit-host/chainlit"
 * @param {string} opts.accessToken     JWT from POST /chainlit/{topic}
 * @param {HTMLElement} opts.container  the .analyst-panel-body element
 * @param {string} [opts.features]      comma list from the request ("chat")
 */
export async function mountChainlitSidebar({
  chainlitServer,
  accessToken,
  container,
  features = "chat",
}) {
  if (!chainlitServer || !accessToken || !container) return;
  if (
    current &&
    current.accessToken === accessToken &&
    document.getElementById(WIDGET_ID)
  ) {
    return;
  }

  // Hard remount — see module docstring.
  removeWidgetDom();
  removeScript();
  current = null;

  // Fetch the skin text alongside the script: injected into the shadow root
  // right after mount so the widget never paints unstyled (its own
  // customCssUrl <link> loads only after the first render).
  // Must run on the host document, not the shadow root — see ensureInterFont.
  ensureInterFont();
  const cssUrl = `${chainlitServer}/public/copilot-abi.css?v=${COPILOT_CSS_VERSION}`;
  const [, cssText] = await Promise.all([
    injectScript(chainlitServer),
    fetch(cssUrl, { referrerPolicy: REFERRER_POLICY })
      .then((r) => (r.ok ? r.text() : null))
      .catch(() => null),
  ]);

  // Drop any persisted thread id so a previous project's conversation can
  // never be restored into this one.
  if (typeof window.clearChainlitCopilotThreadId === "function") {
    window.clearChainlitCopilotThreadId();
  }

  window.mountChainlitWidget({
    chainlitServer,
    accessToken,
    displayMode: "sidebar",
    // Follow the OS/system colour scheme: the widget's ThemeProvider resolves
    // "system" via prefers-color-scheme and re-runs on change. copilot-web2.css
    // only defines the light skin, so dark falls back to the widget's own
    // theme (no custom dark palette).
    theme: "system",
    opened: true,
    customCssUrl: cssUrl,
  });

  const host = document.getElementById(WIDGET_ID);
  if (host) {
    if (cssText && host.shadowRoot) {
      const style = document.createElement("style");
      style.textContent = cssText;
      host.shadowRoot.appendChild(style);
    }
    // Hide the composer when the chat feature is off (parity with the
    // full-page app's features.js, which cannot run inside the widget).
    host.classList.toggle(
      "chat-feature-hidden",
      !String(features).split(",").map((f) => f.trim()).includes("chat")
    );
    // Relocate the widget into the platform's own panel; copilot-web2.css
    // makes the widget's inner panel fill this container.
    container.appendChild(host);
  }

  current = { accessToken };
}

/**
 * Fully remove the widget and its script (project change, page unmount).
 * The next mount starts a genuinely fresh Chainlit session.
 */
export function teardownChainlitSidebar() {
  removeWidgetDom();
  removeScript();
  current = null;
}
